import { useCallback, useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'

import { useCreateProductMessageMutation } from '@/graphql/generated/hooks'
import { CreateProductMessageInput } from '@/graphql/generated/schemas'

/**
 * Return type for useCreateProductMessage hook
 */
export interface UseCreateProductMessageReturnIProps {
  createProductMessage: (
    input: CreateProductMessageInput
  ) => Promise<{ success: boolean; error?: unknown }>
  isCreating: boolean
}

/**
 * Hook that handles creation of product messages with proper error handling.
 */
export const useCreateProductMessage =
  (): UseCreateProductMessageReturnIProps => {
    const [createMutation] = useCreateProductMessageMutation()
    const { showSuccess, showError } = useToasts()
    const [isCreating, setIsCreating] = useState(false)

    const createProductMessage = useCallback(
      async (
        input: CreateProductMessageInput
      ): Promise<{ success: boolean; error?: unknown }> => {
        setIsCreating(true)

        try {
          const result = await createMutation({
            variables: { input },
            refetchQueries: ['ProductMessages'],
            awaitRefetchQueries: true,
          })

          if (!result.data?.createProductMessage?.productMessage) {
            throw new Error('No data returned from mutation')
          }

          showSuccess('Announcement banner created successfully.')

          return { success: true }
        } catch (error) {
          Sentry.captureException(error)
          showError('Failed to create announcement banner.')

          return { success: false, error }
        } finally {
          setIsCreating(false)
        }
      },
      [createMutation, showSuccess, showError]
    )

    return {
      createProductMessage,
      isCreating,
    }
  }
